import { DocumentChunk } from "../../types/interfaces/IConversationSettings";

/**
 * Cleans the gateway response data, extracting relevant information from a Uint8Array representation.
 *
 * @param {Uint8Array | undefined} data - The Uint8Array representing the gateway response data, or undefined if no data is provided.
 *
 * @return {{chunk: string, citations: string[], documentChunks?: DocumentChunk[], batchInfo?: any}} The cleaned text, citations, and document chunks extracted from the response data.
 */
export default function cleanGatewayResponse(
  data: Uint8Array | undefined,
): {chunk: string, citations: string[], documentChunks?: DocumentChunk[], batchInfo?: any} {
  if (!data || data.length === 0) {
    return {chunk: "", citations: []};
  }

  let decoded: string;
  try {
    decoded = new TextDecoder().decode(data);
  } catch (error) {
    console.log("Error decoding response data:", error);
    return {
      chunk: "Binary response received but couldn't be processed.",
      citations: []
    };
  }

  // For debugging - only log first part to avoid console overload
  console.log("Response data length:", decoded.length);
  console.log("Response data sample:", decoded.substring(0, 200) + "...");
  
  // PRIORITY 1: Check for RAG chunks first, even in large responses
  if (decoded.includes('"rag_chunks"') && decoded.includes('"id"')) {
    console.log("DEBUG: Large response contains RAG chunks, attempting extraction first");
    console.log("DEBUG: Response starts with:", decoded.substring(0, 50));
    
    try {
      // First, try to clean up the response if it starts with "data: "
      let cleanedDecoded = decoded;
      if (decoded.startsWith('data: ')) {
        cleanedDecoded = decoded.substring(6); // Remove "data: " prefix
        console.log("DEBUG: Removed 'data: ' prefix, new length:", cleanedDecoded.length);
      }
      
      // Try to parse the entire response as JSON first
      try {
        const fullJson = JSON.parse(cleanedDecoded);
        if (fullJson.rag_chunks && Array.isArray(fullJson.rag_chunks)) {
          console.log("DEBUG: Successfully parsed full JSON with rag_chunks:", fullJson.rag_chunks.length);
          
          // Check if this is a batched response
          if (fullJson.batch_info) {
            console.log("DEBUG: Found batch info:", fullJson.batch_info);
            return {
              chunk: "",
              documentChunks: fullJson.rag_chunks,
              citations: [],
              batchInfo: fullJson.batch_info
            };
          }
          
          return {
            chunk: "",
            documentChunks: fullJson.rag_chunks,
            citations: [],
          };
        }
      } catch (fullParseError) {
        console.log("DEBUG: Full JSON parse failed, trying manual extraction:", fullParseError);
      }
      
      // If full parsing failed, try to extract rag_chunks manually
      const ragStartIndex = cleanedDecoded.indexOf('"rag_chunks"');
      if (ragStartIndex !== -1) {
        console.log("DEBUG: Found rag_chunks at index:", ragStartIndex);
        
        // Find the opening bracket
        const arrayStartIndex = cleanedDecoded.indexOf('[', ragStartIndex);
        if (arrayStartIndex !== -1) {
          // Find the matching closing bracket with proper nesting handling
          let bracketCount = 0;
          let arrayEndIndex = -1;
          let inString = false;
          let escapeNext = false;
          
          for (let i = arrayStartIndex; i < cleanedDecoded.length; i++) {
            const char = cleanedDecoded[i];
            
            if (escapeNext) {
              escapeNext = false;
              continue;
            }
            
            if (char === '\\') {
              escapeNext = true;
              continue;
            }
            
            if (char === '"' && !escapeNext) {
              inString = !inString;
              continue;
            }
            
            if (!inString) {
              if (char === '[') bracketCount++;
              if (char === ']') {
                bracketCount--;
                if (bracketCount === 0) {
                  arrayEndIndex = i;
                  break;
                }
              }
            }
          }
          
          if (arrayEndIndex !== -1) {
            const ragChunksStr = cleanedDecoded.substring(arrayStartIndex, arrayEndIndex + 1);
            try {
              const ragChunksData = JSON.parse(ragChunksStr);
              if (Array.isArray(ragChunksData) && ragChunksData.length > 0) {
                console.log("DEBUG: Successfully extracted rag_chunks manually:", ragChunksData.length);
                return {
                  chunk: "",
                  documentChunks: ragChunksData,
                  citations: [],
                };
              }
            } catch (manualParseError) {
              console.log("DEBUG: Manual extraction failed:", manualParseError);
            }
          }
        }
      }
    } catch (error) {
      console.log("DEBUG: Error in RAG chunk extraction from large response:", error);
    }
  }
  
  // PRIORITY 2: Only process as image data if no RAG chunks were found
  // Direct base64 extraction approach for large responses
  // This is more reliable for chunked or binary data
  if (decoded.length > 10000 || decoded.includes("gpt-image") || decoded.includes("GPT-image")) {
    try {
      // Look for any mention of an image response
      if (decoded.includes("b64_json")) {
        console.log("Found b64_json in response");
      }
      
      // DIRECT FIX: Try to extract both the revised_prompt AND the original model tag
      if (decoded.includes("[GPT-image-1]")) {
        console.log("Found GPT-image-1 tag in response");
        const promptPattern = /\[GPT-image-1\]\s*([^"]+)/;
        const promptMatch = decoded.match(promptPattern);
        if (promptMatch && promptMatch[1]) {
          const extractedPrompt = promptMatch[1].trim();
          console.log("Extracted prompt from GPT-image-1 tag:", extractedPrompt);
        }
      }
      
      // Improved regex pattern that's more tolerant of malformed JSON
      // This looks for any b64_json field with its value
      const b64JsonPattern = /"b64_json"\s*:\s*"([A-Za-z0-9+/=]+)"/;
      const match = decoded.match(b64JsonPattern);
      
      if (match && match[1]) {
        const b64Data = match[1];
        console.log("Found base64 data, length:", b64Data.length);
        
        // Look for prompt in the response
        const promptPattern = /"revised_prompt"\s*:\s*"([^"]+)"/;
        const promptMatch = decoded.match(promptPattern);
        const prompt = promptMatch && promptMatch[1] ? promptMatch[1] : "Generated image";
        console.log("Extracted revised_prompt from JSON:", prompt);
        
        // Verify the data is valid base64 (must be divisible by 4 with proper padding)
        if (b64Data.length % 4 === 0 && /^[A-Za-z0-9+/=]+$/.test(b64Data)) {
          // Create a markdown image tag with the base64 data
          const imageMarkdown = `![${prompt}](data:image/png;base64,${b64Data})`;
          console.log("Created image markdown with base64 data and prompt:", prompt);
          return {
            chunk: imageMarkdown,
            citations: []
          };
        } else {
          console.log("Base64 data validation failed");
        }
      } else {
        console.log("No b64_json match found using pattern");
        
        // Try a more permissive approach for GPT-image-1 responses
        try {
          // Try to parse as JSON
          const jsonObj = JSON.parse(decoded);
          if (jsonObj && jsonObj.data && jsonObj.data[0] && jsonObj.data[0].b64_json) {
            const b64Data = jsonObj.data[0].b64_json;
            console.log("Found b64_json in parsed JSON object:", b64Data.length);
            
            // Extract the original prompt from the request
            const prompt = jsonObj.prompt || jsonObj.data[0].revised_prompt || "Generated image";
            console.log("Found prompt in JSON:", prompt);
            
            // Create a markdown image tag with the base64 data
            const imageMarkdown = `![${prompt}](data:image/png;base64,${b64Data})`;
            console.log("Created image markdown from parsed JSON object with prompt:", prompt);
            return {
              chunk: imageMarkdown,
              citations: []
            };
          }
        } catch (jsonParseError) {
          console.log("Error parsing JSON:", jsonParseError);
          
          // If JSON parsing failed due to truncation, try to extract partial data
          if (jsonParseError instanceof Error && 
              (jsonParseError.message.includes("Unterminated string") || 
               jsonParseError.message.includes("position"))) {
            console.log("Detected truncated JSON, attempting partial extraction");
            
            // Try to extract b64_json data even from truncated JSON
            const partialB64Match = decoded.match(/"b64_json"\s*:\s*"([A-Za-z0-9+/=]+)/);
            if (partialB64Match && partialB64Match[1]) {
              const partialB64 = partialB64Match[1];
              console.log("Extracted partial base64 data from truncated JSON, length:", partialB64.length);
              
              // Try to extract prompt from the available data
              const promptMatch = decoded.match(/"revised_prompt"\s*:\s*"([^"]+)"/);
              const prompt = promptMatch && promptMatch[1] ? promptMatch[1] : "Generated image";
              
              // Only use the partial data if it's substantial (likely a real image)
              if (partialB64.length > 1000) {
                console.log("Using partial base64 data for image generation");
                return {
                  chunk: `![${prompt}](data:image/png;base64,${partialB64})`,
                  citations: []
                };
              } else {
                console.log("Partial base64 data too small, likely incomplete");
              }
            }
          }
        }
      }
      
      // Try another pattern looking for base64 data with iVBOR header (PNG files)
      const pngPattern = /"b64_json"[^"]*"(iVBOR[A-Za-z0-9+/=]+)"/;
      const pngMatch = decoded.match(pngPattern);
      
      if (pngMatch && pngMatch[1]) {
        // Look for prompt in the response
        const promptPattern = /"prompt"\s*:\s*"([^"]+)"/;
        const promptMatch = decoded.match(promptPattern);
        const prompt = promptMatch && promptMatch[1] ? promptMatch[1] : "Generated image";
        
        console.log("Found PNG header in base64 data with prompt:", prompt);
        return {
          chunk: `![${prompt}](data:image/png;base64,${pngMatch[1]})`,
          citations: []
        };
      } else {
        console.log("No PNG header match found");
      }
    } catch (error) {
      console.log("Error extracting base64 data:", error);
    }
    
    // Check if this might be a response with a URL instead of base64
    try {
      const urlPattern = /"url"\s*:\s*"(https?:\/\/[^"]+)"/;
      const urlMatch = decoded.match(urlPattern);
      
      if (urlMatch && urlMatch[1]) {
        const url = urlMatch[1];
        
        // IMPORTANT: Only treat as image URL if it's actually an image, not a document
        // Exclude SharePoint, document URLs, and other non-image URLs
        const isImageUrl = /\.(jpg|jpeg|png|gif|bmp|webp)(\?|$)/i.test(url) && 
                          !url.includes('sharepoint.com') && 
                          !url.includes('/Documents/') &&
                          !url.includes('.pdf');
        
        if (isImageUrl) {
          console.log("Found valid image URL:", url);
          return {
            chunk: `![Generated image](${url})`,
            citations: []
          };
        } else {
          console.log("Found URL but not an image URL, skipping:", url);
        }
      } else {
        console.log("No URL match found");
      }
    } catch (error) {
      console.log("Error extracting URL:", error);
    }
    
    // Special handling for GPT-image responses
    if (decoded.includes("gpt-image") || decoded.includes("GPT-image")) {
      console.log("GPT-image response detected but couldn't extract image data");
      
      // Try to find the original prompt in the response
      let extractedPrompt = "Generated image";
      try {
        // Look for prompt in JSON-like format
        const promptPattern = /"prompt"\s*:\s*"([^"]+)"/;
        const promptMatch = decoded.match(promptPattern);
        if (promptMatch && promptMatch[1]) {
          extractedPrompt = promptMatch[1];
          console.log("Found original prompt in GPT-image response:", extractedPrompt);
        }
      } catch (e) {
        console.log("Error extracting prompt:", e);
      }
      
      // Last resort: try to find any base64 encoded image data
      try {
        // Look for a large chunk of base64 data (longer than 1000 chars)
        const base64Chunks = decoded.match(/[A-Za-z0-9+/=]{1000,}/g);
        if (base64Chunks && base64Chunks.length > 0) {
          // Sort by length and take the longest one, which is likely the image
          const longestChunk = base64Chunks.sort((a, b) => b.length - a.length)[0];
          console.log("Found potential base64 chunk, length:", longestChunk.length);
          
          // Check if it looks like a valid base64 string
          if (longestChunk.length % 4 === 0) {
            return {
              chunk: `![${extractedPrompt}](data:image/png;base64,${longestChunk})`,
              citations: []
            };
          }
        }
      } catch (e) {
        console.log("Error in last-resort base64 extraction:", e);
      }
      
      return {
        chunk: `![${extractedPrompt}](Image generated but couldn't be displayed)`,
        citations: []
      };
    }
  }

  // For smaller responses, try standard JSON parsing
  if (decoded.length < 100000 && 
      (decoded.trim().startsWith('{') || decoded.trim().startsWith('['))) {
    try {
      const image = JSON.parse(decoded);
      console.log("Successfully parsed JSON response:", Object.keys(image));
      
      // DIRECT FIX: Log the entire response structure for debugging
      if (image && image.data && image.data[0]) {
        console.log("JSON response data[0] keys:", Object.keys(image.data[0]));
        console.log("JSON revised_prompt:", image.data[0].revised_prompt);
      }
      
      if (image?.data !== undefined && image.data[0] !== undefined) {
        console.log("Found image data in JSON response");
        
        // Extract the original prompt first if available
        let originalPrompt = "Generated image";
        if (image.prompt) {
          originalPrompt = image.prompt;
          console.log("Found original prompt in response root:", originalPrompt);
        }
        
        // Check if this is a GPT-image response by model_type
        const isGptImage = image.data[0].model_type === "gpt-image-1";
        if (isGptImage) {
          console.log("Detected GPT-image-1 by model_type field");
        }
        
        // Get the revised prompt without any tag manipulation
        const revisedPrompt = image.data[0].revised_prompt || "";
        
        // Process URL-based response (like DALL-E)
        if (image.data[0].url) {
          const altText = revisedPrompt || originalPrompt;
          console.log("Found image URL with prompt:", altText);
          return {chunk: `![${altText}](${image.data[0].url})`, citations: []};
        } 
        // Process base64 response
        else if (image.data[0].b64_json) {
          // First try to get revised_prompt
          let altText = revisedPrompt;
          
          // Use original prompt or revised prompt
          if (!altText || altText === "Generated image") {
            altText = originalPrompt;
            console.log("Using original prompt for image:", altText);
          } else {
            console.log("Using revised prompt for image:", altText);
          }
          
          console.log("Found b64_json in JSON response, length:", image.data[0].b64_json.length);
          return {chunk: `![${altText}](data:image/png;base64,${image.data[0].b64_json})`, citations: []};
        } else {
          console.log("No URL or b64_json found in image data");
        }
      } else {
        console.log("No image data found in JSON response");
      }
    } catch (error) {
      // For smaller payloads, we expect valid JSON, so log this error
      console.log("Error in JSON parsing:", error);
    }
  }
  
  // Try to handle a regular text stream response (non-image)
  try {
    let text = "";
    let citations: string[] = [];
    let documentChunks: DocumentChunk[] = [];
    
    for (let l of decoded.split("\n")) {
      l = l.trim();
      l = l.replace("data: ", "");
      if (l === "") continue;
      
      try {
        const parsed = JSON.parse(l);
        
        // Debug: Log what we're parsing to see the structure
        console.log("DEBUG: Parsed JSON keys:", Object.keys(parsed));
        
        // Handle dedicated rag_chunks chunk (no content, just chunks)
        if (parsed.rag_chunks && Array.isArray(parsed.rag_chunks)) {
          console.log("DEBUG: Found rag_chunks in dedicated chunk:", parsed.rag_chunks.length);
          
          // Check if this is a batched response
          if (parsed.batch_info) {
            console.log("DEBUG: Found batch info in streaming response:", parsed.batch_info);
            return {
              chunk: "",
              documentChunks: parsed.rag_chunks,
              citations: [],
              batchInfo: parsed.batch_info
            };
          }
          
          documentChunks = parsed.rag_chunks;
          console.log("DEBUG: Processed document chunks:", documentChunks.length);
          continue; // Skip to next chunk, don't process as content
        }
        
        // Handle content chunks
        if (parsed.choices && parsed.choices[0] && parsed.choices[0].delta) {
          const deltaContent = parsed.choices[0].delta.content;
          if (deltaContent) {
            text += deltaContent;
          }
        }
        
        // Handle citations
        if (parsed.citations && Array.isArray(parsed.citations)) {
          citations = [...citations, ...parsed.citations];
        }
      } catch (parseError) {
        // Check if this chunk contains rag_chunks data even if JSON parsing failed
        if (l.includes('"rag_chunks"') && l.includes('"id"')) {
          console.log("DEBUG: Detected rag_chunks in unparseable chunk, attempting recovery");
          console.log("DEBUG: Chunk length:", l.length, "First 200 chars:", l.substring(0, 200));
          
          // Try to extract rag_chunks from malformed JSON
          try {
            // First, try to clean up the chunk if it's truncated or malformed
            let cleanChunk = l;
            
            // If the chunk doesn't end properly, try to find a reasonable ending point
            if (!cleanChunk.endsWith('}') && !cleanChunk.endsWith(']')) {
              // Look for the last complete object or array
              const lastBraceIndex = cleanChunk.lastIndexOf('}');
              const lastBracketIndex = cleanChunk.lastIndexOf(']');
              const cutoffIndex = Math.max(lastBraceIndex, lastBracketIndex);
              
              if (cutoffIndex > 0) {
                cleanChunk = cleanChunk.substring(0, cutoffIndex + 1);
                console.log("DEBUG: Truncated chunk to last complete structure");
              }
            }
            
            // Try to parse the cleaned chunk
            try {
              const cleanedParsed = JSON.parse(cleanChunk);
              if (cleanedParsed.rag_chunks && Array.isArray(cleanedParsed.rag_chunks)) {
                console.log("DEBUG: Successfully parsed cleaned chunk with rag_chunks:", cleanedParsed.rag_chunks.length);
                documentChunks = cleanedParsed.rag_chunks;
                continue;
              }
            } catch (cleanParseError) {
              console.log("DEBUG: Cleaned chunk parse failed, trying extraction methods");
            }
            
            // Look for the rag_chunks array in the malformed JSON
            const ragChunksMatch = cleanChunk.match(/"rag_chunks":\s*(\[[\s\S]*?\])(?=\s*[,}]|$)/);
            if (ragChunksMatch && ragChunksMatch[1]) {
              console.log("DEBUG: Attempting to parse extracted rag_chunks array");
              
              // Try to parse just the rag_chunks array
              const ragChunksData = JSON.parse(ragChunksMatch[1]);
              if (Array.isArray(ragChunksData)) {
                console.log("DEBUG: Successfully parsed rag_chunks from malformed JSON:", ragChunksData.length);
                documentChunks = ragChunksData;
                continue; // Successfully extracted rag_chunks, skip further processing
              }
            }
            
            // If that fails, try to find individual chunk objects with more robust pattern
            const chunkPattern = /\{"id":\s*"[^"]+",\s*"document_title":\s*"[^"]*",\s*"document_source":\s*"[^"]*",\s*"content":\s*"[^"]*"[^}]*\}/g;
            const chunkMatches = cleanChunk.match(chunkPattern);
            
            if (chunkMatches && chunkMatches.length > 0) {
              console.log("DEBUG: Found individual chunks in malformed JSON, reconstructing");
              const reconstructedChunks = chunkMatches.map(chunkStr => {
                try {
                  // Clean up any trailing commas or incomplete JSON
                  let cleanChunkStr = chunkStr.replace(/,\s*$/, '');
                  
                  // Ensure it ends with a closing brace
                  if (!cleanChunkStr.endsWith('}')) {
                    cleanChunkStr += '}';
                  }
                  
                  return JSON.parse(cleanChunkStr);
                } catch (e) {
                  console.log("DEBUG: Failed to parse individual chunk:", e);
                  return null;
                }
              }).filter(chunk => chunk !== null);
              
              if (reconstructedChunks.length > 0) {
                console.log("DEBUG: Successfully reconstructed chunks from malformed JSON:", reconstructedChunks.length);
                documentChunks = reconstructedChunks;
                continue;
              }
            }
            
            // Last resort: try to extract document information and create chunks
            const titleMatches = cleanChunk.match(/"document_title":\s*"([^"]+)"/g);
            if (titleMatches && titleMatches.length > 0) {
              console.log("DEBUG: Found document titles, creating chunks with available data");
              const extractedChunks: DocumentChunk[] = titleMatches.map((titleMatch, index) => {
                const titleExtract = titleMatch.match(/"document_title":\s*"([^"]+)"/);
                if (titleExtract && titleExtract[1]) {
                  const title = titleExtract[1];
                  
                  // Try to extract other fields near this title - expand search range
                  const titleIndex = cleanChunk.indexOf(titleMatch);
                  const contextStart = Math.max(0, titleIndex - 500);
                  const contextEnd = Math.min(cleanChunk.length, titleIndex + 2000);
                  const context = cleanChunk.substring(contextStart, contextEnd);
                  
                  // Extract ID if available
                  const idMatch = context.match(/"id":\s*"([^"]+)"/);
                  const id = idMatch ? idMatch[1] : `recovered_${index}`;
                  
                  // Extract document source if available
                  const sourceMatch = context.match(/"document_source":\s*"([^"]+)"/);
                  const source = sourceMatch ? sourceMatch[1] : "Unknown";
                  
                  // Extract URL if available - look for SharePoint URLs specifically
                  const urlMatch = context.match(/"url":\s*"([^"]+)"/);
                  let url = urlMatch ? urlMatch[1] : undefined;
                  
                  // Also try to find URLs in the broader chunk context
                  if (!url) {
                    const sharePointUrlMatch = cleanChunk.match(/https:\/\/[^"]*sharepoint[^"]*\.pdf/);
                    if (sharePointUrlMatch) {
                      url = sharePointUrlMatch[0];
                      console.log("DEBUG: Found SharePoint URL in broader context:", url);
                    }
                  }
                  
                  // Extract content - look for longer content strings
                  let content = "Content truncated due to parsing error";
                  const contentMatch = context.match(/"content":\s*"([^"]{50,})/);
                  if (contentMatch && contentMatch[1]) {
                    // Take more content and clean it up
                    let extractedContent = contentMatch[1];
                    
                    // If content seems truncated, try to get more from the broader context
                    if (extractedContent.length < 100) {
                      const broaderContentMatch = cleanChunk.match(/"content":\s*"([^"]{100,})/);
                      if (broaderContentMatch && broaderContentMatch[1]) {
                        extractedContent = broaderContentMatch[1];
                      }
                    }
                    
                    // Clean up escaped characters and truncate nicely
                    extractedContent = extractedContent
                      .replace(/\\n/g, ' ')
                      .replace(/\\"/g, '"')
                      .replace(/\s+/g, ' ')
                      .trim();
                    
                    if (extractedContent.length > 200) {
                      content = extractedContent.substring(0, 200) + "...";
                    } else {
                      content = extractedContent;
                    }
                  }
                  
                  console.log(`DEBUG: Extracted chunk - ID: ${id}, Title: ${title}, Source: ${source}, URL: ${url ? 'found' : 'missing'}, Content length: ${content.length}`);
                  
                  return {
                    id: id,
                    document_title: title,
                    document_source: source,
                    content: content,
                    url: url
                  } as DocumentChunk;
                }
                return null;
              }).filter((chunk): chunk is DocumentChunk => chunk !== null);
              
              if (extractedChunks.length > 0) {
                console.log("DEBUG: Created chunks from extracted data:", extractedChunks.length);
                documentChunks = extractedChunks;
                continue;
              }
            }
          } catch (recoveryError) {
            console.log("DEBUG: Could not recover rag_chunks from malformed JSON:", recoveryError);
          }
        }
        
        // The backend already sends RAG chunks in clean, separate dedicated chunks
        // followed by clean LLM content chunks. No need for content recovery or guessing.
        // If we can't parse a chunk as JSON, it's likely corrupted and should be skipped.
        
        console.log("Error parsing JSON:", parseError);
        
        // Only attempt image extraction if this clearly contains image data
        if (l.includes("b64_json") && l.includes("iVBOR")) {
          console.log("Potential image data detected, attempting extraction...");
          
          const b64Match = l.match(/"b64_json":\s*"([^"]+)"/);
          if (b64Match && b64Match[1].length > 1000) {
            console.log("Extracted base64 image data, length:", b64Match[1].length);
            return {
              chunk: `![Generated image](data:image/png;base64,${b64Match[1]})`,
              documentChunks: [],
              citations: [],
            };
          }
        }
        
        // For all other parsing errors, skip the chunk - the backend sends clean data
        console.log("Skipping unparseable chunk - backend sends clean separated data");
        continue;
      }
    }

    console.log("DEBUG: Returning chunks:", documentChunks.length, "text length:", text.length);
    return {
      chunk: text,
      documentChunks,
      citations,
    };
  } catch (error) {
    console.error("Error processing stream data:", error);
  }

  // If we get here, provide a generic message
  return {
    chunk: "Response received. Processing...",
    citations: []
  };
}
